'use client';

import { useState } from 'react';
import { SmartFormattedMessage } from '@/components/smart-formatted-message';
import { useRouteErrorCapture } from '@/hooks/use-route-error-capture';
import { ErrorLanguageProvider } from './error-language-provider';

function clearClientStorage() {
  try {
    localStorage.clear();
  } catch {
    // localStorage may be unavailable
  }
  try {
    sessionStorage.clear();
  } catch {
    // sessionStorage may be unavailable
  }
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [isClearing, setIsClearing] = useState(false);
  const { deploymentSkew } = useRouteErrorCapture(error, 'root');

  function handleClearSession() {
    setIsClearing(true);
    clearClientStorage();
    // Server-side route clears the session cookie, then redirects to /sign-in
    window.location.href = '/api/auth/clear-session';
  }

  return (
    <ErrorLanguageProvider>
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
          <h1 className="mb-2 text-xl font-semibold text-gray-900">
            Something went wrong
          </h1>
          <p className="mb-6 text-sm text-gray-600">
            {deploymentSkew ? (
              <SmartFormattedMessage
                defaultMessage="This page needs an update. Reload it, then try your action again."
                description="Instruction after an open tab calls an action from an older release"
              />
            ) : (
              'An unexpected error occurred. Please try again or clear your session if the problem persists.'
            )}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={deploymentSkew ? () => window.location.reload() : reset}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {deploymentSkew ? (
                <SmartFormattedMessage
                  defaultMessage="Reload page"
                  description="Button to load the current release after a stale Server Action"
                />
              ) : (
                'Try Again'
              )}
            </button>
            <button
              onClick={handleClearSession}
              disabled={isClearing}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {isClearing ? 'Clearing...' : 'Clear Session & Sign In'}
            </button>
          </div>
        </div>
      </div>
    </ErrorLanguageProvider>
  );
}
